import React, {
	useState, useEffect, useMemo, useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { noop } from 'underscore';
import classNames from 'classnames';
import DatePicker from 'react-datepicker';
import { datadogLogs } from '@datadog/browser-logs';
import Socket from '../../../connection/socket';
import { FindUser } from '../../../connection/home';
import {
	CreateInvitations,
	NewInvitation,
} from '../../../connection/meeting';
import { ChannelAuthMethod, GreenroomState } from '../../../stores/types/types';
import * as InviteActions from '../../../stores/actions/invitations';
import type { Invitation } from '../../../stores/types/invitations';
import type { ShowSession, ShowSessionType } from '../../../stores/types/session';
import { MODAL_INFO_ACTIONS } from '../../../stores/reducers/modal';
import * as ModalActions from '../../../stores/actions/modal';
import * as SessionActions from '../../../stores/actions/session';
import * as NotificationBuilder from '../../../utils/notifications-builder';
import { CreateIOSNotification } from '../../../utils/notification-builder-ios';
import { isValidEmail } from '../../../utils/utils';
import Icon, { ICONS, COLORS } from '../../general-components/icon';
import SelectSmall from '../../general-components/select-small';
import * as Alerts from '../../general-components/alert-service';
import { SelectGroupOption } from '../../general-components/select-group';
import Avatar from '../../general-components/avatar';
import SelectUsers from '../../general-components/select-users';
import { InvitedPresenter, PRESENTER_ROLE } from '../../app-entry/producer/meeting-modal';
import InviteOptions, {
	getInviteOptionIndexByValue,
	ssoOnlyInviteOptions,
	userInviteOptions,
} from '../../meta-menu/invite-options';
import { FillMaskForInvites } from '../../../connection/masks';

interface Props {
	showSession: ShowSession
	showSessionTypes?: ShowSessionType[]
	showInvitations: Invitation[]
	socket: Socket
	modalInfoAction: string
}

export default function EventSettings(props: Props) {
	const {
		showSession,
		showSessionTypes,
		showInvitations,
		socket,
		modalInfoAction,
	} = props;

	const showSessionTypeOptions: SelectGroupOption[] = !showSessionTypes
		? []
		: showSessionTypes?.reduce((selectGroupOptions, showSessionType) => {
			// If the showSessionType is not a broadcast or if the showSession
			// is a broadcast saved before the ability to do so was removed.
			if (showSessionType.id !== 4 || showSession?.session_type_id === 4) {
				return [...selectGroupOptions, {
					label: showSessionType.name,
					value: showSessionType.id,
					description: '',
					className: 'text-transform-capitalize',
				}];
			}

			return selectGroupOptions;
		}, [] as SelectGroupOption[]);

	const dispatch = useDispatch();
	const user = useSelector((state: GreenroomState) => state.SessionReducer.user);
	const token = useSelector((state: GreenroomState) => state.SessionReducer.token);
	const meeting = useSelector((state: GreenroomState) => state.SessionReducer.meeting);
	const homeMeeting = useSelector((state: GreenroomState) => state.HomeReducer.homeMeeting);
	const ssoOnlyDomains = useSelector((s: GreenroomState) => s.InvitationsReducer.ssoOnlyDomains);

	const channelAuth = homeMeeting?.channel_auth || [ChannelAuthMethod.EmailPassword];

	const [showSessionType, setShowSessionType] = useState<number>(
		showSession ? showSession.session_type_id - 1 : 0,
	);

	const [startDate, setStartDate] = useState<Date>(new Date());
	const [endDate, setEndDate] = useState<Date>(new Date());
	const [newPresenterIsWatcher, setNewPresenterIsWatcher] = useState(false);
	const [emailValid, setEmailValid] = useState(false);
	const [inviteQueue, setInviteQueue] = useState<NewInvitation[]>([]);
	const [showSessionInviteQueue, setShowSessionInviteQueue] = useState<string[]>([]);
	const [meetingInfo, setMeetingInfo] = useState<any>(meeting || homeMeeting);
	const [saveDisabled, setSaveDisabled] = useState(false);
	const [newPresenterOptionIndex, setNewPresenterOptionIndex] = useState<number>(0);
	const [newPresenterEmailIsSsoOnly, setNewPresenterEmailIsSsoOnly] = useState<boolean>(false);
	const [newPresUserSelectedAuth, setNewPresUserSelectedAuth] = useState<ChannelAuthMethod>(
		channelAuth.includes(ChannelAuthMethod.SSO)
			? ChannelAuthMethod.SSO
			: ChannelAuthMethod.EmailPassword,
	);

	const emailField = useRef<HTMLInputElement>(null);
	const titleField = useRef<HTMLInputElement>(null);

	const tempProfileInvite: NewInvitation[] = [];

	const showError = (message: string) => {
		Alerts.showAlert({
			message: `Error ${modalInfoAction === MODAL_INFO_ACTIONS.ADD ? 'adding' : 'editing'} event`,
			description: message,
			type: 'error',
			duration: 3000,
		});
	};

	useEffect(() => {
		switch (modalInfoAction) {
			case MODAL_INFO_ACTIONS.ADD:
				// eslint-disable-next-line no-case-declarations
				const roundedStart = new Date(new Date().getTime() + 30 * 60000);

				roundedStart.setMilliseconds(Math.round(roundedStart.getMilliseconds() / 1000) * 1000);
				roundedStart.setSeconds(Math.round(roundedStart.getSeconds() / 60) * 60);
				roundedStart.setMinutes(Math.round(roundedStart.getMinutes() / 5) * 5);

				setStartDate(roundedStart);
				setEndDate(new Date(roundedStart.getTime() + 30 * 60000));
				break;
			case MODAL_INFO_ACTIONS.EDIT:
			case MODAL_INFO_ACTIONS.DELETE:
				if (titleField && titleField.current && showSession.title) {
					titleField.current.value = showSession.title;
				}

				setStartDate(new Date(showSession.start_time));
				setEndDate(new Date(showSession.end_time));
				setShowSessionInviteQueue(showSession.invitations);
				break;
			case MODAL_INFO_ACTIONS.INVITE:
				emailField.current?.focus();
				break;
			default:
				break;
		}
	}, [showSession, modalInfoAction]);

	useEffect(() => {
		setMeetingInfo(meeting || homeMeeting);

		if (
			modalInfoAction === MODAL_INFO_ACTIONS.ADD
			&& titleField
			&& titleField.current
			&& meetingInfo
			&& meetingInfo.title
		) {
			titleField.current.value = meetingInfo.title;
		}
	}, [homeMeeting, meeting]);

	useEffect(() => {
		// if a user has been invited elsewhere and they
		// are in the invite queue, remove them from the queue
		if (
			modalInfoAction === MODAL_INFO_ACTIONS.INVITE
			&& inviteQueue.length > 0
		) {
			setInviteQueue(inviteQueue.filter(
				(newInvite: NewInvitation) => !showInvitations.some(
					(invitation) => invitation.profile.email === newInvite.email,
				),
			));
		}
	}, [showInvitations]);

	const saveShowSession = (
		showSessionTypeId: number,
		startDate2: Date,
		endDate2: Date,
		title: string,
		invitationQueue: string[],
	) => {
		if (!meetingInfo) return;
		if (!token) return;
		if (!user) return;

		if (new Date(startDate2).valueOf() > new Date(endDate2).valueOf()) {
			showError('Start time cannot be after end time.');
			return;
		} if (!title || title.trim().length === 0) {
			showError('Please enter a title for this session.');
			return;
		}

		let addedEmails = [...invitationQueue];
		let removedEmails: string[] = [];

		if (modalInfoAction === MODAL_INFO_ACTIONS.EDIT) {
			if (showSession.start_time === startDate2 && showSession.end_time === endDate2) {
				// if editing a showSession, filter out previously invited users if the date has not changed
				addedEmails = addedEmails.filter(
					(email) => !showSession.invitations.some((invitation) => invitation === email),
				);
			}

			removedEmails = showSession.invitations.filter(
				(invitation) => !invitationQueue?.includes(invitation),
			);
		}

		const newShowSession: ShowSession = {
			session_type_id: showSessionTypeId,
			meeting: meetingInfo.meeting,
			start_time: startDate2,
			end_time: endDate2,
			id: modalInfoAction === MODAL_INFO_ACTIONS.EDIT ? showSession.id : 0,
			title,
			invitations: showInvitations.filter(
				(invitation) => invitationQueue?.some(
					(inviteEmail) => inviteEmail === invitation.profile.email,
				),
			).map((invitation) => invitation.profile.email),
		};

		if (modalInfoAction === MODAL_INFO_ACTIONS.ADD) {
			dispatch(SessionActions.addShowSession(token, newShowSession));
		} else if (modalInfoAction === MODAL_INFO_ACTIONS.EDIT) {
			dispatch(SessionActions.editShowSession(token, newShowSession));
		}

		const showSessionType2 = showSessionTypes && showSessionTypes[showSessionTypeId]
			? showSessionTypes[showSessionTypeId].name : 'show session';

		socket?.sendMessage('show-sessions-update', { token, meeting: meetingInfo.meeting });
		NotificationBuilder.showSessionCreatedNotification(
			token,
			user,
			addedEmails,
			meetingInfo,
			newShowSession.start_time,
			showSessionType2,
		);
		addedEmails.forEach((email) => socket?.sendMessage('new-invite', '', `temp-${email}`));
		removedEmails.forEach((email) => socket?.sendMessage('revoke-invite', '', `temp-${email}`));
		dispatch(ModalActions.setModalOpen(false));
	};

	const removeShowSession = (showSession2: ShowSession) => {
		if (!token) return;

		dispatch(SessionActions.deleteShowSession(token, showSession2));
		socket?.sendMessage('show-sessions-update', { token, meeting: meetingInfo.meeting });
		showSession2.invitations.forEach((invitation) => socket?.sendMessage('revoke-invite', '', `temp-${invitation}`));
		dispatch(ModalActions.setModalOpen(false));
	};

	const invitePresenters = async (inviteQueue2: NewInvitation[]) => {
		if (saveDisabled) return;
		if (inviteQueue2.length > 0) {
			setSaveDisabled(true);
			try {
				if (!meetingInfo || !token || !user) {
					// eslint-disable-next-line no-throw-literal
					throw '';
				}

				let invites;

				if (inviteQueue2.length > 0) {
					invites = inviteQueue2.map(({
						email,
						// eslint-disable-next-line camelcase
						watcher_only,
						authentication,
					}) => ({
						email,
						// eslint-disable-next-line camelcase
						watcher_only,
						...(authentication && { authentication }),
					}));
				} else {
					invites = tempProfileInvite.map(({
						email,
						// eslint-disable-next-line camelcase
						watcher_only,
						authentication,
					}) => ({
						email,
						// eslint-disable-next-line camelcase
						watcher_only,
						...(authentication && { authentication }),
					}));
				}

				let newInvitations = [];

				newInvitations = await CreateInvitations(
					token,
					meetingInfo.meeting,
					meetingInfo.channel_name,
					meetingInfo.title,
					meetingInfo.channel,
					invites,
				);

				dispatch(InviteActions.addInvitations(newInvitations));

				if (newInvitations.length === 0) {
					datadogLogs.logger.warn('Details page => invite users => no invitations created');
				}

				const presenterInvitations = newInvitations.filter(
					(invite: Invitation) => !invite.watcher_only,
				).map((invite: Invitation) => invite.profile.email);
				const viewerInvitations = newInvitations.filter(
					(invite: Invitation) => invite.watcher_only,
				).map((invite: Invitation) => invite.profile.email);

				if (presenterInvitations.length > 0) {
					NotificationBuilder.inviationPresenterToGreenroomNotification(
						token,
						user,
						presenterInvitations,
						meetingInfo,
					);
					CreateIOSNotification(token, presenterInvitations.join(), '', 'You’ve been invited as a presenter');
				}

				if (viewerInvitations.length > 0) {
					NotificationBuilder.inviationViewerToGreenroomNotification(
						token,
						user,
						viewerInvitations,
						meetingInfo,
					);
					CreateIOSNotification(token, viewerInvitations.join(), '', 'You’ve been invited as a viewer');
				}

				socket?.sendMessage('invites-update', [...showInvitations, ...newInvitations]);
				const emails = invites.map((i) => i.email)
				await FillMaskForInvites(meetingInfo.meeting, emails)
				dispatch(ModalActions.setModalOpen(false));
			} catch (e) {
				setSaveDisabled(false);
				Alerts.showAlert({
					message: 'Unable to invite presenters',
					description: 'We ran into an issue inviting presenters to this greenroom. Please try again.',
					type: 'error',
				});
			}
		} else {
			Alerts.showAlert({
				message: 'Please invite one or more presenters.',
				duration: 2000,
				type: 'warning',
			});
		}
	};

	const inviteOptions = useMemo(() => (
		userInviteOptions
			.filter((option) => option.authType !== ChannelAuthMethod.SSO)
			.filter((option) => channelAuth.includes(option.authType))
	), [channelAuth, userInviteOptions]);

	const newPresenterMenu = useMemo(() => (
		newPresenterEmailIsSsoOnly ? ssoOnlyInviteOptions : inviteOptions
	), [newPresenterEmailIsSsoOnly]);

	const newPresenterAuthType = useMemo(() => (
		userInviteOptions.find((option) => (
			option.value === newPresenterMenu[newPresenterOptionIndex].value
		))?.authType ?? ChannelAuthMethod.EmailPassword
	), [newPresenterMenu, newPresenterOptionIndex]);

	const createNewInvite = async (): Promise<NewInvitation | null> => {
		if (!token || !emailField.current?.value) {
			return null;
		}

		if (!emailValid) {
			setSaveDisabled(false);
			Alerts.showAlert({
				message: 'Please enter a valid email address.',
				duration: 2000,
				type: 'warning',
			});
			return null;
		}

		const trimmedEmail = emailField.current.value.toLowerCase().trim();
		const exists = (
			showInvitations.some((invite: Invitation) => invite.profile.email.trim() === trimmedEmail)
			|| inviteQueue.some((invite) => invite.email === trimmedEmail)
		);

		if (exists) {
			Alerts.showAlert({
				message: 'User already invited.',
				duration: 2000,
				type: 'warning',
			});
			return null;
		}

		const { profile } = await FindUser(token, trimmedEmail);

		return {
			...profile,
			watcher_only: newPresenterIsWatcher,
			authentication: newPresenterAuthType,
			isSsoOnly: newPresenterEmailIsSsoOnly,
		};
	};

	const handleEmptyQueue = async () => {
		if (saveDisabled) return;
		if (emailField.current?.value === '') {
			Alerts.showAlert({
				message: 'Please invite one or more presenters.',
				duration: 2000,
				type: 'warning',
			});
			return;
		}

		const newInvite = await createNewInvite();
		if (!newInvite) return;

		invitePresenters([newInvite]).then(noop);
		if (emailField.current) {
			emailField.current.value = '';
			setEmailValid(false);
		}
	};

	function checkEmail() {
		if (emailField.current) {
			const email = emailField.current.value.trim();
			const emailDomain = email.split('@')[1];
			const emailShouldBeSsoOnly = (ssoOnlyDomains || []).includes(emailDomain);

			setEmailValid(isValidEmail(email));

			if (emailShouldBeSsoOnly && !newPresenterEmailIsSsoOnly) {
				setNewPresenterEmailIsSsoOnly(true);
				setNewPresenterOptionIndex(getInviteOptionIndexByValue(
					ssoOnlyInviteOptions,
					newPresenterIsWatcher,
					ChannelAuthMethod.SSO,
				));
			} else if (newPresenterEmailIsSsoOnly && !emailShouldBeSsoOnly) {
				setNewPresenterEmailIsSsoOnly(false);
				setNewPresenterOptionIndex(getInviteOptionIndexByValue(
					inviteOptions,
					newPresenterIsWatcher,
					newPresUserSelectedAuth,
				));
			}
		}
	}

	function changeTypeInForm(value: string) {
		// platformAuth ? value is a string : value is a boolean
		const isWatcherOnly = value.includes('viewer');
		const isSSO = value.includes('sso');
		const menuIndex = newPresenterMenu.findIndex((option) => option.value === value);

		setNewPresenterOptionIndex(menuIndex === -1 ? 0 : menuIndex);
		setNewPresUserSelectedAuth(isSSO ? ChannelAuthMethod.SSO : ChannelAuthMethod.EmailPassword);

		if (isWatcherOnly === newPresenterIsWatcher) { return; }
		setNewPresenterIsWatcher(isWatcherOnly);
	}

	function changeTypeInList(newPresenterType: string, invitation: NewInvitation) {
		const isWatcherOnly = newPresenterType.includes('viewer');
		const isSSO = newPresenterType.includes('sso');
		const newQueue = inviteQueue.map((queued: NewInvitation) => {
			if (invitation.email === queued.email) {
				return {
					...queued,
					watcher_only: isWatcherOnly,
					authentication: isSSO
						? ChannelAuthMethod.SSO
						: ChannelAuthMethod.EmailPassword,
				};
			}

			return queued;
		});

		setInviteQueue(newQueue);
	}

	async function queueInvite(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		setSaveDisabled(true);

		const newInvite = await createNewInvite();
		if (newInvite) {
			setInviteQueue([...inviteQueue, newInvite]);
			emailField.current!.value = '';
			setEmailValid(false);
			setNewPresenterOptionIndex(0);
			setNewPresenterEmailIsSsoOnly(false);
			setNewPresUserSelectedAuth(
				channelAuth.includes(ChannelAuthMethod.SSO)
					? ChannelAuthMethod.SSO
					: ChannelAuthMethod.EmailPassword,
			);
		}
		setSaveDisabled(false);
	}

	function removeFromQueue(email: string) {
		setInviteQueue((inviteQueue2) => inviteQueue2.filter(
			(presenter: NewInvitation) => presenter.email !== email,
		));
	}

	function remove() {
		if (modalInfoAction === MODAL_INFO_ACTIONS.DELETE) {
			removeShowSession(showSession);
		} else {
			dispatch(ModalActions.setModalOpen(false));
		}
	}

	const save = () => {
		if (modalInfoAction === MODAL_INFO_ACTIONS.INVITE) {
			if (inviteQueue.length === 0) {
				handleEmptyQueue().then(noop);
			} else invitePresenters(inviteQueue).then(noop);
		} else if (modalInfoAction === MODAL_INFO_ACTIONS.DELETE) {
			dispatch(ModalActions.setModalOpen(false));
		} else {
			saveShowSession(
				showSessionType + 1,
				startDate,
				endDate,
				titleField.current!.value,
				showSessionInviteQueue,
			);
		}
	};

	function parseShowInvitationsIntoInvitedPresenters() {
		const invitedPresenters = showInvitations.map((invite: Invitation) => ({
			...invite.profile,
			avatar: invite.profile.avatar,
			role: invite.watcher_only ? PRESENTER_ROLE.VIEWER : PRESENTER_ROLE.PRESENTER,
		})) as InvitedPresenter[];

		return invitedPresenters;
	}

	function handleSettingShowSessionInviteQueue(invites: InvitedPresenter[]) {
		const newShowSessionInviteQueue = invites.map((invitedPresenter) => invitedPresenter.email);
		setShowSessionInviteQueue(newShowSessionInviteQueue);
	}

	function getSelectedUsersFromShowSessionInviteQueue() {
		const selectedUsers = [] as InvitedPresenter[];
		const invitedPresenters = parseShowInvitationsIntoInvitedPresenters();

		const len = invitedPresenters.length;
		let i = 0;
		for (i; i < len; i += 1) {
			const invitedPresenter = invitedPresenters[i];
			if (showSessionInviteQueue.some((email: string) => email === invitedPresenter.email)) {
				selectedUsers.push(invitedPresenter);
			}
		}

		return selectedUsers;
	}

	const modalHeader = useMemo(() => {
		switch (modalInfoAction) {
			case MODAL_INFO_ACTIONS.ADD:
				return (
					<>
						<h1>Schedule new</h1>
						<p>Schedule additional times to meet.</p>
					</>
				);
			case MODAL_INFO_ACTIONS.EDIT:
				return <h1>Edit Session</h1>;
			case MODAL_INFO_ACTIONS.DELETE:
				return (
					<>
						<h1>Are you sure you want to delete this session?</h1>
						<p>You won’t be able to restore it.</p>
					</>
				);
			case MODAL_INFO_ACTIONS.INVITE:
				return <h1>Invite Presenter</h1>;
			default:
				return <h1></h1>;
		}
	}, [modalInfoAction]);

	const queuedPresenters = useMemo(() => inviteQueue.map(
		(presenter: NewInvitation, index: number) => (
			<div className="invite-presenter-row" key={index}>
				<Avatar
					noBorder={false}
					user={presenter}
					size={'thirtytwo'}
				/>
				<div className="presenter-name">
					{(presenter.first_name || presenter.last_name)
						&& <label className="invite-user-name">{presenter.first_name} {presenter.last_name}</label>
					}
				</div>
				<div className="presenter-email">
					<span>{presenter.email}</span>
				</div>
				<div className="presenter-controls">
					<InviteOptions
						menu={presenter.isSsoOnly ? ssoOnlyInviteOptions : inviteOptions}
						menuOptionIndex={getInviteOptionIndexByValue(
							presenter.isSsoOnly ? ssoOnlyInviteOptions : inviteOptions,
							presenter.watcher_only,
							presenter.authentication,
						)}
						onChange={(newPresenterType) => (
							changeTypeInList(newPresenterType, presenter)
						)}
					/>
					<button onClick={() => removeFromQueue(presenter.email)}>
						<Icon name={ICONS.CLOSE} size={12} color={COLORS.DEFAULT_GRAY} />
					</button>
				</div>
			</div>
		),
	), [inviteQueue]);

	return (
		<div className={classNames('info-modal', { remove: modalInfoAction === MODAL_INFO_ACTIONS.DELETE, invite: modalInfoAction === MODAL_INFO_ACTIONS.INVITE })}>
			<div className={classNames('info-modal-header', 'margin-bottom-medium', { remove: modalInfoAction === MODAL_INFO_ACTIONS.DELETE, invite: modalInfoAction === MODAL_INFO_ACTIONS.INVITE })}>
				{modalHeader}
			</div>
			{(modalInfoAction === MODAL_INFO_ACTIONS.ADD || modalInfoAction === MODAL_INFO_ACTIONS.EDIT)
			&& (
				<div className="info-modal-show-session">
					<div className="input-container margin-bottom-medium">
						<div className="input-header">
							<span>Type*</span>
						</div>
						<div className="input-body">
							<SelectSmall
								options={showSessionTypeOptions}
								onChange={(showSessionTypeId: number) => setShowSessionType(showSessionTypeId - 1)}
								label={'show-session-type'}
								selected={showSessionTypeOptions[showSessionType]}
								dropdown={true}
								optionHeight={48}
							/>
						</div>
					</div>
					<div className="input-container margin-bottom-medium">
						<div className="input-header">
							<span>Title*</span>
						</div>
						<div className="input-body">
							<input type="text" placeholder="Name your session..." ref={titleField} />
						</div>
					</div>
					<div className="start-end-container margin-bottom-medium">
						<div className="input-header">
							<span>Starts*</span>
							<span>Ends*</span>
						</div>
						<div className="start-end">
							<div className="show-session-date">
								<DatePicker
									selected={startDate}
									onChange={(date) => {
										if (date) {
											setStartDate(date);
										}
									}}
									dateFormat="MMM d yyyy"
								/>
								<Icon name={ICONS.CALENDAR} color={COLORS.WHITE} size={13.33} />
							</div>
							<div className="show-session-time">
								<DatePicker
									selected={startDate}
									onChange={(date) => {
										if (date) {
											setStartDate(date);
										}
									}}
									dateFormat="h:mm aa"
									showTimeSelect
									showTimeSelectOnly
									timeIntervals={5}
								/>
								<Icon name={ICONS.CLOCK_ALT} color={COLORS.WHITE} size={13.33} />
							</div>
							<span className="start-end-divider">–</span>
							<div className="show-session-date">
								<DatePicker
									selected={endDate}
									onChange={(date) => {
										if (date) {
											setEndDate(date);
										}
									}}
									dateFormat="MMM d yyyy"
								/>
								<Icon name={ICONS.CALENDAR} color={COLORS.WHITE} size={13.33} />
							</div>
							<div className="show-session-time">
								<DatePicker
									selected={endDate}
									onChange={(date) => {
										if (date) {
											setEndDate(date);
										}
									}}
									dateFormat="h:mm aa"
									showTimeSelect
									showTimeSelectOnly
									timeIntervals={5}
								/>
								<Icon name={ICONS.CLOCK_ALT} color={COLORS.WHITE} size={13.33} />
							</div>
						</div>
					</div>
					{showInvitations.length > 0 && <div className="input-container width-full">
						<div className="input-header">
							<span>Team</span>
						</div>
						<div className="input-body">
							<SelectUsers
								users={parseShowInvitationsIntoInvitedPresenters()}
								onChange={(newInvites) => handleSettingShowSessionInviteQueue(newInvites)}
								selected={getSelectedUsersFromShowSessionInviteQueue()}
							/>
						</div>
					</div>}
				</div>
			)}
			{modalInfoAction === MODAL_INFO_ACTIONS.INVITE && (
				<div className="info-modal-invite margin-bottom-medium">
					<div className="invite-user-form">
						<div className="input-header">
							<span>Team*</span>
						</div>
						<form className="input-form" onSubmit={queueInvite}>
							<input
								type="text"
								className="truncate-overflow"
								onChange={checkEmail}
								placeholder="Type email..."
								ref={emailField}
							/>
							<InviteOptions
								menu={newPresenterMenu}
								menuOptionIndex={newPresenterOptionIndex}
								onChange={changeTypeInForm}
							/>
						</form>
					</div>
					<div className="invite-presenters-list">
						{queuedPresenters}
					</div>
				</div>
			)}
			<div className="info-modal-bottom">
				<div className="info-modal-buttons">
					<button className={classNames('info-gray', { remove: modalInfoAction === MODAL_INFO_ACTIONS.DELETE })} onClick={remove}>
						{modalInfoAction === MODAL_INFO_ACTIONS.DELETE ? 'Delete Session' : 'Cancel'}
					</button>
					<button className={classNames('info-green', { remove: modalInfoAction === MODAL_INFO_ACTIONS.DELETE, disabled: saveDisabled })} onClick={save}>
						{modalInfoAction === MODAL_INFO_ACTIONS.DELETE ? 'Cancel' : 'Save'}
					</button>
				</div>
			</div>
		</div>
	);
}
